/** @format */

import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { _$, Col, Row } from '@king-design/react'
import OverviewLineChart, { OverviewLineChartPoint } from '../../components/OverviewLineChart'
import { useAsync } from 'react-use'
import { getMetricsData } from 'src/api/ApiAIObservability'
import { fillMetricPoints } from './constant'
import useSize from 'src/hooks/useSize'

interface OverviewProps {
  className?: string
  serviceName?: string
  startTime: number
  endTime: number
  aggregate: number
  region?: string
}

export const METRIC_TITLE_ENUM = {
  SessionCount: '会话数',
  TokenUsage: 'Token消耗数',
  InvocationCount: '调用数',
  ErrorCount: '错误数',
  ErrorRate: '错误率'
}

const Overview: React.FC<OverviewProps> = ({
  className,
  serviceName,
  startTime,
  endTime,
  aggregate,
  region
}) => {
  const chartRowRef = useRef<HTMLDivElement>(null)
  const { width: chartRowWidth } = useSize(chartRowRef)
  const chartColHeight = useMemo(() => {
    if (!chartRowWidth) {
      return 300
    }
    const colWidth = (chartRowWidth - 48) / 3
    return (colWidth * 9) / 16
  }, [chartRowWidth])

  const xAxis = useAsync(async () => {
    if (startTime && endTime && aggregate) {
      const result = await getMetricsData({
        ServiceName: serviceName,
        StartTime: startTime,
        EndTime: endTime,
        Step: `${aggregate}s`,
        Region: region
      })

      return (result ?? []).reduce((acc, item) => {
        acc[item.Metric] = item.Points?.length
          ? fillMetricPoints(item.Points, startTime, endTime, aggregate)
          : []
        return acc
      }, {} as Record<string, OverviewLineChartPoint[]>)
    }
  }, [serviceName, startTime, endTime, aggregate, region])

  return (
    <div className={className}>
      <div ref={chartRowRef} className="chart-row overview-charts">
        <Row gutter="16">
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('会话数')}
              data={xAxis.value?.SessionCount}
              colHeight={chartColHeight}
            />
          </Col>
          {serviceName ? (
            <Col span={8} className="overview-chart-col">
              <OverviewLineChart
                title={_$('Token消耗数')}
                data={xAxis.value?.TokenUsage}
                colHeight={chartColHeight}
              />
            </Col>
          ) : null}
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('调用数')}
              data={xAxis.value?.InvocationCount}
              colHeight={chartColHeight}
            />
          </Col>
          {serviceName ? (
            <Col span={12} className="overview-chart-col">
              <OverviewLineChart
                title={_$('错误数')}
                data={xAxis.value?.ErrorCount}
                colHeight={chartColHeight}
              />
            </Col>
          ) : null}
          <Col span={serviceName ? 12 : 8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('错误率')}
              data={xAxis.value?.ErrorRate}
              yPercent
              colHeight={chartColHeight}
            />
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default styled(Overview)`
  .chart-row.overview-charts {
    width: 100%;

    .k-col {
      margin-top: 16px;
    }
  }
`
